<template>
    <FullScreenOverlay @close="closeFunction">
        <div class="page-wrapper">
            <router-view
                @is-single-scorecard="setIsSingleScorecardAutoGen"
                @is-closing-scorecard="setIsClosingScorecard"
                @clicked-delete="isDeleteScorecardModalOpen = true"
            />
        </div>
    </FullScreenOverlay>

    <!-- Cover Settings Modal -->
    <Modal v-model="showEditCoverModal" title="Cover Settings" width="64.25rem" no-padding>
        <template #content>
            <div class="edit-cover-modal-content-container" @keyup.enter="submitScorecardSettings">
                <div class="section">
                    <AccountInfoForm
                        :register-submit-function="registerAccountInfoSubmitFunction"
                        :image-upload-disabled="coverPageDataLoading"
                        :account-name="coverPageData?.accountName ?? ''"
                        :account-logo-url="coverPageData?.accountLogoUrl ?? ''"
                        :on-account-logo-url-update="updateAccountLogoUrl"
                        :website="coverPageData?.website ?? null"
                        :industry="coverPageData?.industry ?? null"
                        :location="coverPageData?.location ?? null"
                        autofocusInput
                    />
                    <Spacer height="2.5rem" />
                    <SenderInfoForm
                        v-if="coverPageData"
                        :register-submit-function="registerSenderInfoFormSubmitFunction"
                        :sender-user-id="userId ?? undefined"
                        :sender-email="coverPageData.senderEmail"
                        :sender-name="coverPageData.senderName"
                        :sender-logo-url="coverPageData.senderLogoUrl"
                        :on-sender-logo-url-update="updateSenderLogoUrl"
                        :image-upload-disabled="coverPageDataLoading"
                        :is-auto-generated="isSingleScorecardAutoGen"
                    />
                </div>
            </div>
        </template>
        <template #buttons>
            <oButton color="white" @clicked="showEditCoverModal = false">Cancel</oButton>
            <Spacer width="0.625rem" />
            <oButton color="blue" @clicked="submitScorecardSettings">
                Update Cover Settings
            </oButton>
        </template>
    </Modal>

    <!-- Delete Scorecard  -->
    <DeleteScorecard
        :scorecard-id="activeScorecardId"
        @close-modal="isDeleteScorecardModalOpen = false"
    ></DeleteScorecard>
</template>

<script lang="ts" setup>
import { computed, ref, Ref, provide } from 'vue'
import { useRouter } from 'vue-router'
import { Routes } from '@/router/routes'

import { oButton, Spacer, Modal } from '@opteo/components-next'

import FullScreenOverlay from '@/components/util/FullScreenOverlay.vue'
import AccountInfoForm from '@/components/scorecard/settings/AccountInfoForm.vue'
import SenderInfoForm from '@/components/scorecard/settings/SenderInfoForm.vue'
import DeleteScorecard from '@/components/scorecard/DeleteScorecard.vue'

import { useScorecardCoverEditModal } from '@/composition/toolkit/scorecard/useScorecardCoverEditModal'
import { useUser } from '@/composition/user/useUser'

const user = useUser()
// userId is of weird RemovableRef type and was acting weirdly on the template, so doing this instead
const userId = user.userId.value
const router = useRouter()

const isSingleScorecardAutoGen = ref(false)
const isScorecardUnavailable = ref(false)

/* Delete Scorecard */
const isSingleScorecard = computed(
    () => router.currentRoute.value.name === Routes.ToolkitScorecardSingle
)
const activeScorecardId = computed(() =>
    isSingleScorecard ? (router.currentRoute.value?.params?.scorecardId as string) : ''
)

const isDeleteScorecardModalOpen = ref(false)

provide<{ isDeleteScorecardModalOpen: Ref<boolean> }>('delete-scorecard', {
    isDeleteScorecardModalOpen,
})

const closeFunction = () => {
    if (router.currentRoute.value.name === Routes.ToolkitScorecardList) {
        router.push({ name: Routes.Toolkit })
    } else {
        router.push({ name: Routes.ToolkitScorecardList })
    }
}

function setIsSingleScorecardAutoGen(singleScorecardData: {
    scorecardId: string
    isAutoGenerated: boolean
    isScorecardUnavailable: boolean
}) {
    isSingleScorecardAutoGen.value = singleScorecardData?.isAutoGenerated
    isScorecardUnavailable.value = singleScorecardData?.isScorecardUnavailable
}

const setIsClosingScorecard = () => {
    isSingleScorecardAutoGen.value = false
    isScorecardUnavailable.value = false
}

const {
    registerAccountInfoSubmitFunction,
    registerSenderInfoFormSubmitFunction,
    submitScorecardSettings,
    updateAccountLogoUrl,
    updateSenderLogoUrl,
    showEditCoverModal,
    coverPageData,
    coverPageDataLoading,
} = useScorecardCoverEditModal()
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

.page-wrapper {
    padding: 4.5rem 3rem 0 3rem;
    max-width: 1148px;
    margin: 0 auto;
}

.edit-cover-modal-content-container .section {
    @include pt-48;
    @include pb-52;
    max-width: 34.5rem;
    margin: 0 auto;
}
</style>
